import { z } from 'zod';

/**
 * Web Push Subscription-Daten wie vom Browser geliefert.
 */
export const PushSubscriptionKeysSchema = z.object({
  p256dh: z.string().min(1),
  auth: z.string().min(1),
});

/** TypeScript Type für die Subscription-Keys */
export type PushSubscriptionKeys = z.infer<typeof PushSubscriptionKeysSchema>;

/**
 * Request-Body für POST /push/subscribe.
 * Enthält die vollständige Browser-Push-Subscription.
 */
export const SubscribePushSchema = z.object({
  endpoint: z.string().url('Ungültige Endpoint-URL'),
  keys: PushSubscriptionKeysSchema,
  userAgent: z.string().max(512).optional(),
});

/** TypeScript Type für Push-Subscribe-Request */
export type SubscribePush = z.infer<typeof SubscribePushSchema>;

/**
 * Request-Body für DELETE /push/unsubscribe.
 */
export const UnsubscribePushSchema = z.object({
  endpoint: z.string().url('Ungültige Endpoint-URL'),
});

/** TypeScript Type für Push-Unsubscribe-Request */
export type UnsubscribePush = z.infer<typeof UnsubscribePushSchema>;

/**
 * Nutzlast einer Push-Benachrichtigung.
 */
export const PushNotificationPayloadSchema = z.object({
  title: z.string().min(1).max(100),
  body: z.string().min(1).max(500),
  icon: z.string().url().optional(),
  url: z.string().optional(),
  data: z.object({}).passthrough().optional(),
});

/** TypeScript Type für Push-Payload */
export type PushNotificationPayload = z.infer<typeof PushNotificationPayloadSchema>;

/**
 * Request-Body für POST /push/send (an einzelnen User).
 */
export const SendPushToUserSchema = z.object({
  userId: z.string().min(1),
  notification: PushNotificationPayloadSchema,
});

/** TypeScript Type für Send-to-User-Request */
export type SendPushToUser = z.infer<typeof SendPushToUserSchema>;

/**
 * Request-Body für POST /push/send-all (Broadcast an alle Subscriber).
 */
export const SendPushToAllSchema = z.object({
  notification: PushNotificationPayloadSchema,
});

/** TypeScript Type für Send-all-Request */
export type SendPushToAll = z.infer<typeof SendPushToAllSchema>;

/**
 * Antwort-Schema nach Versand einer Push-Benachrichtigung.
 */
export const PushSendResultSchema = z.object({
  sent: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  errors: z.array(z.string()),
});

/** TypeScript Type für Push-Versand-Ergebnis */
export type PushSendResult = z.infer<typeof PushSendResultSchema>;

/**
 * Antwort-Schema für GET /push/vapid-key.
 */
export const VapidPublicKeyResponseSchema = z.object({
  publicKey: z.string().min(1),
});

/** TypeScript Type für VAPID Public Key Response */
export type VapidPublicKeyResponse = z.infer<typeof VapidPublicKeyResponseSchema>;
